import {ErrorBoundary} from 'react-error-boundary';
import React, {Suspense} from 'react';
import {HistorySettingProps} from '@di-shared/features/history/setting/HistorySetting/index';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useApiError from '@di-shared/features/common/useApiError';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {SettingType} from '@di-shared/api/di/type';
import usePendingHandler from '@shared/usePendingHandler';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import HistoryLock from '@di-shared/features/history/setting/HistoryLock';

export default function ({vin}: HistorySettingProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <HistoryLock
          vin={vin}
          fallbackLoading={<FallbackLoading />}
          fallbackError={<FallbackError />}
        >
          <Inner vin={vin} />
        </HistoryLock>
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: HistorySettingProps) {
  const {t} = useInferableTranslation();
  const {resolveError} = useApiError();
  const {data: lockData, refetch: lockRefetch} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.vehicleSettingController.getVehicleSetting), vin, SettingType.LOCK_DRIVE_RECORD],
    queryFn: () =>
      diApi.vehicleSettingController.getVehicleSetting({
        vin,
        settingType: SettingType.LOCK_DRIVE_RECORD,
      }),
    select: ({data}) => data,
  });

  const [updateLockSetting] = usePendingHandler(
    async () => {
      const {isCanceled, isAuth} = await ccWebviewUtils.authRequest();
      if (isCanceled || !isAuth) {
        return;
      }

      await diApi.vehicleSettingController.updateVehicleSetting({
        vin,
        settingType: SettingType.LOCK_DRIVE_RECORD,
        requestBody: {
          enabled: !lockData.enabled,
        },
      });
      await lockRefetch();
    },
    async (error) => {
      await resolveError(error);
    }
  );

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10088')}</h1>
      </Header>
      <main
        className="content page_setting"
        id="content"
      >
        <section className="section">
          <h2 className="section_title body_large_b">{t('T10090')}</h2>
          <ul className="toggle_list">
            <li className="toggle_item">
              <div className="toggle_trigger">
                <div className="toggle_text">
                  <p className="toggle_title title_xxsmall"> {t('T10091')}</p>
                  <p className="toggle_desc body_medium_r"> {t('T10092')}</p>
                </div>
                <div className="toggle_box">
                  <input
                    id="toggle01"
                    className="input"
                    name="toggleGroupSetting01"
                    type="checkbox"
                    checked={lockData.enabled}
                    onChange={updateLockSetting}
                  />
                  <label
                    className="label"
                    htmlFor="toggle01"
                  >
                    {t('T10091')}
                  </label>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10088')}</h1>
      </Header>
      <main
        className="content page_setting"
        id="content"
      >
        <section className="section">
          <h2
            className="section_title body_large_b skeleton"
            style={{width: '160px', marginBottom: '10px'}}
          >
            {t('T10090')}
          </h2>
          <ul className="toggle_list">
            <li
              className="toggle_item skeleton"
              style={{marginBottom: '10px'}}
            >
              <div className="toggle_trigger">
                <div className="toggle_text">
                  <p className="toggle_title title_xxsmall"> {t('T10091')}</p>
                  <p className="toggle_desc body_medium_r"> {t('T10092')}</p>
                </div>
                <div className="toggle_box">
                  <input
                    id="toggle01"
                    className="input"
                    name="toggleGroupSetting01"
                    type="checkbox"
                    checked={false}
                  />
                  <label
                    className="label"
                    htmlFor="toggle01"
                  >
                    {t('T10091')}
                  </label>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T10088')}</h1>
      </Header>
      <main
        className="content page_setting"
        id="content"
      >
        <section className="section">
          <h2 className="section_title body_large_b">{t('T10090')}</h2>
          <ul className="toggle_list">
            <li className="toggle_item">
              <div className="toggle_trigger">
                <div className="toggle_text">
                  <p className="toggle_title title_xxsmall"> {t('T10091')}</p>
                  <p className="toggle_desc body_medium_r"> {t('T10092')}</p>
                </div>
                <div className="toggle_box">
                  <input
                    id="toggle01"
                    className="input"
                    name="toggleGroupSetting01"
                    type="checkbox"
                    checked={false}
                  />
                  <label
                    className="label"
                    htmlFor="toggle01"
                  >
                    {t('T10091')}
                  </label>
                </div>
              </div>
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
